import { ToastNotification } from '@carbon/react';
import { useEffect, useState, useCallback, useRef, useMemo } from 'react';
import { createPortal } from 'react-dom';
import type { NotificationConfig } from './notify.tsx';

interface AnimatedNotificationProps {
  show: boolean;
  kind?:
    | 'error'
    | 'info'
    | 'info-square'
    | 'success'
    | 'warning'
    | 'warning-alt';
  title: string;
  messages: string[];
  onClose: () => void;
  autoHideDelay?: number; // in milliseconds, 0 means no auto-hide
  position?:
    | 'top-center'
    | 'top-right'
    | 'top-left'
    | 'bottom-center'
    | 'bottom-right'
    | 'bottom-left';
  width?: string;
  duration?: number; // animation duration in milliseconds
  offsetFromEdge?: string; // Tailwind distance class (e.g., 'top-24', 'top-[250px]', 'bottom-8')
}

export function AnimatedNotification({
  show,
  kind = 'error',
  title,
  messages,
  onClose,
  autoHideDelay = 4000,
  position = 'top-center',
  width = 'w-96',
  duration = 350,
  offsetFromEdge,
}: AnimatedNotificationProps) {
  const [isVisible, setIsVisible] = useState(false);
  const [shouldRender, setShouldRender] = useState(false);
  const autoHideTimerRef = useRef<NodeJS.Timeout | null>(null);

  // Position classes - using transform for centering to avoid CSS conflicts
  const positionClasses = useMemo(
    () => ({
      'top-center': '',
      'top-right': 'right-4',
      'top-left': 'left-4',
      'bottom-center': 'bottom-4',
      'bottom-right': 'bottom-4 right-4',
      'bottom-left': 'bottom-4 left-4',
    }),
    [],
  );

  // Position styles for reliable centering
  const getPositionStyle = (pos: string) => {
    if (pos === 'top-center' || pos === 'bottom-center') {
      return {
        left: '50%',
      };
    }
    return {};
  };

  // Use custom offset or default based on position
  const defaultOffset = position.startsWith('top') ? 'top-24' : 'bottom-24';
  const finalOffset = offsetFromEdge || defaultOffset;
  const slideDistance = position.startsWith('top') ? '-80px' : '80px';

  // Clear timers
  const clearTimers = useCallback(() => {
    if (autoHideTimerRef.current) {
      clearTimeout(autoHideTimerRef.current);
      autoHideTimerRef.current = null;
    }
  }, []);

  // Handle close
  const handleClose = useCallback(() => {
    clearTimers();
    setIsVisible(false);
    setTimeout(() => {
      setShouldRender(false);
      onClose();
    }, duration);
  }, [onClose, clearTimers, duration]);

  // Show/hide effect
  useEffect(() => {
    if (show) {
      setShouldRender(true);
      // Small delay to allow DOM to render
      setTimeout(() => {
        setIsVisible(true);

        // Debug positioning
        if (typeof document !== 'undefined') {
          // Find the notification element to get its actual computed position
          setTimeout(() => {
            const notificationEl = document.querySelector('[role="alert"]');
            if (notificationEl) {
              const rect = notificationEl.getBoundingClientRect();
              const computedStyle = getComputedStyle(notificationEl);

              // console.log('📱 Notification positioned:', {
              //   position,
              //   finalOffset,
              //   width,
              //   positionClasses: positionClasses[position],
              //   viewportWidth: window.innerWidth,
              //   documentBodyWidth: document.body.offsetWidth,
              //   documentElementWidth: document.documentElement.offsetWidth,
              //   bodyOverflow: getComputedStyle(document.body).overflow,
              //   htmlOverflow: getComputedStyle(document.documentElement).overflow,
              //   // Actual computed position
              //   actualLeft: rect.left,
              //   actualRight: rect.right,
              //   actualWidth: rect.width,
              //   centerPoint: rect.left + (rect.width / 2),
              //   viewportCenter: window.innerWidth / 2,
              //   isActuallyCentered: Math.abs((rect.left + rect.width / 2) - (window.innerWidth / 2)) < 5,
              //   computedLeft: computedStyle.left,
              //   computedTransform: computedStyle.transform,
              // });
            }
          }, 100);
        }
      }, 50);

      // Auto-hide timer
      if (autoHideDelay > 0) {
        autoHideTimerRef.current = setTimeout(handleClose, autoHideDelay);
      }
    } else {
      handleClose();
    }

    return clearTimers;
  }, [
    show,
    autoHideDelay,
    handleClose,
    clearTimers,
    position,
    finalOffset,
    width,
    positionClasses,
  ]);

  // Cleanup on unmount
  useEffect(() => {
    return clearTimers;
  }, [clearTimers]);

  // Message rendering
  const messageElements = useMemo(() => {
    if (messages.length === 0) return null;

    return (
      <div className="mt-2 space-y-1">
        {messages.map((message, index) => (
          <div key={index} className="flex items-center text-sm">
            <span className="mr-2 flex-shrink-0">•</span>
            <span>{message}</span>
          </div>
        ))}
      </div>
    );
  }, [messages]);

  if (!shouldRender) return null;

  // Create CSS custom property for width override
  const getWidthCSSVar = (widthClass: string): Record<string, string> => {
    // Only handle arbitrary values that need special CSS override
    if (widthClass.includes('w-[') && widthClass.includes('px]')) {
      const match = widthClass.match(/w-\[(\d+)px\]/);
      if (match) {
        return { '--toast-width': `${match[1]}px` } as Record<string, string>;
      }
    }
    return {};
  };

  const notificationElement = (
    <div
      className={`fixed ${positionClasses[position]} ${finalOffset} z-[9999] ${width}`}
      role="alert"
      aria-live="polite"
      style={{
        transition: `transform ${duration}ms ease-out, opacity ${duration}ms ease-out`,
        transform: isVisible
          ? position === 'top-center' || position === 'bottom-center'
            ? 'translateX(-50%) translateY(0px)'
            : 'translateY(0px)'
          : position === 'top-center' || position === 'bottom-center'
            ? `translateX(-50%) translateY(${slideDistance})`
            : `translateY(${slideDistance})`,
        opacity: isVisible ? 1 : 0,
        ...getPositionStyle(position),
        ...getWidthCSSVar(width),
      }}
    >
      <div className="notification-wrapper">
        <style>{`
          .notification-wrapper .cds--toast-notification {
            width: var(--toast-width, auto) !important;
            min-width: var(--toast-width, auto) !important;
            max-width: var(--toast-width, auto) !important;
          }
        `}</style>
        <ToastNotification
          kind={kind}
          title={title}
          onClose={handleClose}
          hideCloseButton={false}
        >
          {messageElements}
        </ToastNotification>
      </div>
    </div>
  );

  // Only create portal on client-side to avoid hydration mismatches
  if (typeof document === 'undefined') {
    return notificationElement;
  }

  // Use portal to render directly to document body, bypassing container constraints
  return createPortal(notificationElement, document.body);
}

// Performance-optimized notification state interface
interface NotificationState {
  show: boolean;
  kind: AnimatedNotificationProps['kind'];
  title: string;
  messages: string[];
  config?: NotificationConfig;
}

// Hook for easier state management with performance optimizations
export function useAnimatedNotification() {
  const [notification, setNotification] = useState<NotificationState>({
    show: false,
    kind: 'info',
    title: '',
    messages: [],
    config: undefined,
  });

  // Performance: Memoized notification methods to prevent recreation
  const showNotification = useCallback(
    (
      title: string,
      messages: string | string[],
      kind: AnimatedNotificationProps['kind'] = 'info',
      config?: NotificationState['config'],
    ) => {
      setNotification({
        show: true,
        kind,
        title,
        messages: Array.isArray(messages) ? messages : [messages],
        config,
      });
    },
    [],
  );

  const hideNotification = useCallback(() => {
    setNotification((prev) => ({ ...prev, show: false }));
  }, []);

  // Performance: Memoized convenience methods
  const showError = useCallback(
    (
      title: string,
      messages: string | string[],
      config?: NotificationState['config'],
    ) => showNotification(title, messages, 'error', config),
    [showNotification],
  );

  const showSuccess = useCallback(
    (
      title: string,
      messages: string | string[],
      config?: NotificationState['config'],
    ) => showNotification(title, messages, 'success', config),
    [showNotification],
  );

  const showWarning = useCallback(
    (
      title: string,
      messages: string | string[],
      config?: NotificationState['config'],
    ) => showNotification(title, messages, 'warning', config),
    [showNotification],
  );

  const showInfo = useCallback(
    (
      title: string,
      messages: string | string[],
      config?: NotificationState['config'],
    ) => showNotification(title, messages, 'info', config),
    [showNotification],
  );

  // Performance: Memoize return object to prevent unnecessary re-renders
  return useMemo(
    () => ({
      notification,
      showNotification,
      hideNotification,
      showError,
      showSuccess,
      showWarning,
      showInfo,
    }),
    [
      notification,
      showNotification,
      hideNotification,
      showError,
      showSuccess,
      showWarning,
      showInfo,
    ],
  );
}
